// ==================== קובץ SettingsPage.jsx - הגרסה השלמה והסופית ====================
import React, { useState, useEffect } from 'react';
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { db, resetQueue, resetPots } from "./firebase"; // ודא שהנתיב ל-firebase נכון
import { getAuth, onAuthStateChanged } from "firebase/auth";
import LoginPage from "./LoginPage";

// --- ייבוא כל הרכיבים הנדרשים ---
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker';
import dayjs from 'dayjs';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';

// --- הגדרות עיצוב ---
const cacheRtl = createCache({ key: 'muirtl', stylisPlugins: [prefixer, rtlPlugin] });
const theme = createTheme({ direction: 'rtl' });


// --- הרכיב המרכזי שמכיל את כל הלוגיקה והתצוגה ---
const SettingsContent = ({ userId }) => {
    // 1. ניהול State פנימי
    const [shopSettings, setShopSettings] = useState(null); 
    
    // 2. טעינת ההגדרות מ-Firestore
    const defaultSettings = {
        status: { isOpen: true, reopenMessage: "החנות סגורה כרגע." },
        automation: { isEnabled: false, openDay: '5', openTime: '10:00', closeDay: '5', closeTime: '15:00' }
    };

    useEffect(() => {
        if (!userId) return;
        const docRef = doc(db, "users", userId, "settings", "main");
        const unsubscribe = onSnapshot(docRef, async (docSnap) => {
            if (docSnap.exists()) {
                setShopSettings(docSnap.data());
            } else {
                // Create default settings if not exist
                await setDoc(docRef, defaultSettings);
                setShopSettings(defaultSettings);
            }
        });
        return () => unsubscribe();
    }, [userId]);

    // 3. הגדרת כל הפונקציות (Handlers)
    const handleAutomationChange = (e) => {
        const { name, value, type, checked } = e.target;
        setShopSettings(prev => ({ ...prev, automation: { ...prev.automation, [name]: type === 'checkbox' ? checked : value } }));
    };

    const handleStatusChange = (e) => {
        const { name, value } = e.target;
        setShopSettings(prev => ({ ...prev, status: { ...prev.status, [name]: value } }));
    };

    const handleTimePickerChange = (newValue, timeType) => {
        const formattedTime = newValue ? dayjs(newValue).format('HH:mm') : '00:00';
        setShopSettings(prev => ({ ...prev, automation: { ...prev.automation, [timeType]: formattedTime } }));
    };

    const handleToggleShopStatus = (status) => {
        setShopSettings(prev => ({...prev, status: {...prev.status, isOpen: status}}));
    };

    const handleSaveSettings = async () => {
        if (!shopSettings) return;
        const docRef = doc(db, "users", userId, "settings", "main");
        try {
            await setDoc(docRef, shopSettings, { merge: true });
            alert("ההגדרות נשמרו בהצלחה!");
        } catch (error) {
            console.error("Error saving settings: ", error);
            alert("שגיאה בשמירת ההגדרות.");
        }
    };

    const handleResetQueue = async () => {
        if (window.confirm("האם לאפס את כל מספרי התור?")) {
            await resetQueue(userId).then(() => alert("התור אופס בהצלחה!")).catch(err => alert("שגיאה באיפוס התור."));
        }
    };

    const handleResetPots = async () => {
        if (window.confirm("האם למלא את הסירים ל-100%?")) {
            await resetPots(userId).then(() => alert("הסירים מולאו בהצלחה!")).catch(err => alert("שגיאה במילוי הסירים."));
        }
    };

    // 4. הצגת הודעת טעינה
    if (!shopSettings) {
        return <div className="text-white text-center p-10 text-xl">טוען הגדרות...</div>;
    }
    
    // 5. JSX - החלק הוויזואלי של העמוד
    const daysOfWeek = [ { value: '0', label: 'יום ראשון' }, { value: '1', label: 'יום שני' }, { value: '2', label: 'יום שלישי' }, { value: '3', label: 'יום רביעי' }, { value: '4' , label: 'יום חמישי' }, { value: '5', label: 'יום שישי' }, { value: '6', label: 'שבת' } ];
    const formElementStyles = { '& .MuiInputBase-root': { backgroundColor: 'rgb(55 65 81 / 1)', color: 'white' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgb(75 85 99 / 1)' }, '& .MuiSvgIcon-root': { color: 'white' }, "& label.Mui-focused": { color: '#A0AEC0' }, "& label": { color: '#A0AEC0' } };
    const blockKeyboard = (event) => event.preventDefault();

    return (
        <div className="bg-gray-800 text-white rounded-xl p-6 md:p-8 shadow-2xl max-w-2xl mx-auto text-right">
            <h2 className="text-3xl font-bold mb-8 text-amber-400 text-center">הגדרות</h2>
            
            <div className="mb-6 p-4 bg-gray-700 rounded-lg">
                <h3 className="text-lg font-semibold mb-3">סטטוס חנות ידני</h3>
                <div className="flex items-center justify-center gap-4">
                    <button onClick={() => handleToggleShopStatus(true)} className={`px-6 py-2 rounded-lg font-semibold transition-colors ${shopSettings.status.isOpen ? 'bg-green-500 text-white' : 'bg-gray-600 hover:bg-gray-500'}`}>פתח חנות</button>
                    <button onClick={() => handleToggleShopStatus(false)} className={`px-6 py-2 rounded-lg font-semibold transition-colors ${!shopSettings.status.isOpen ? 'bg-red-500 text-white' : 'bg-gray-600 hover:bg-gray-500'}`}>סגור חנות</button>
                </div>
                <div className="mt-4">
                    <label htmlFor="reopenMessage" className="block text-sm text-gray-400 mb-1">הודעה כשהחנות סגורה:</label>
                    <input id="reopenMessage" name="reopenMessage" type="text" value={shopSettings.status.reopenMessage} onChange={handleStatusChange} placeholder="החנות סגורה, נתראה בקרוב!" className="w-full p-2 bg-gray-600 border border-gray-500 rounded-md text-right"/>
                </div>
            </div>

            <div className="mb-6 p-4 bg-gray-700 rounded-lg">
                <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold">סטטוס חנות אוטומטי</h3>
                    <label className="flex items-center cursor-pointer">
                        <span className="text-sm mr-3">{shopSettings.automation.isEnabled ? 'פעיל' : 'כבוי'}</span>
                        <div className="relative">
                            <input type="checkbox" name="isEnabled" checked={shopSettings.automation.isEnabled} onChange={handleAutomationChange} className="sr-only" />
                            <div className="block bg-gray-600 w-14 h-8 rounded-full"></div>
                            <div className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition ${shopSettings.automation.isEnabled ? 'transform translate-x-6 bg-green-400' : ''}`}></div>
                        </div>
                    </label>
                </div>
                <div className={`transition-all duration-300 ease-in-out overflow-hidden ${shopSettings.automation.isEnabled ? 'max-h-96' : 'max-h-0'}`}>
                    <div className="grid grid-cols-2 gap-4 pt-2">
                        <FormControl fullWidth sx={formElementStyles}>
                            <InputLabel>יום פתיחה</InputLabel>
                            <Select name="openDay" value={shopSettings.automation.openDay} label="יום פתיחה" onChange={handleAutomationChange}>
                                {daysOfWeek.map(day => <MenuItem key={day.value} value={day.value}>{day.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <MobileTimePicker ampm={false} label="שעת פתיחה" value={dayjs(`2022-04-17T${shopSettings.automation.openTime || '00:00'}`)} onChange={(newValue) => handleTimePickerChange(newValue, 'openTime')} slotProps={{ textField: { onKeyDown: blockKeyboard, sx: formElementStyles } }} />
                        <FormControl fullWidth sx={formElementStyles}>
                            <InputLabel>יום סגירה</InputLabel>
                            <Select name="closeDay" value={shopSettings.automation.closeDay} label="יום סגירה" onChange={handleAutomationChange}>
                                {daysOfWeek.map(day => <MenuItem key={day.value} value={day.value}>{day.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <MobileTimePicker ampm={false} label="שעת סגירה" value={dayjs(`2022-04-17T${shopSettings.automation.closeTime || '00:00'}`)} onChange={(newValue) => handleTimePickerChange(newValue, 'closeTime')} slotProps={{ textField: { onKeyDown: blockKeyboard, sx: formElementStyles } }} />
                    </div>
                </div>
            </div>

            <div className="mb-6 p-4 bg-gray-700 rounded-lg">
                <h3 className="text-lg font-semibold mb-3">פעולות נוספות</h3>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                    <button onClick={handleResetQueue} className="bg-yellow-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-yellow-700">אפס תור</button>
                    <button onClick={handleResetPots} className="bg-orange-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-orange-700">מלא סירים</button>
                </div>
            </div>

            <div className="flex justify-center items-center mt-8">
                <button onClick={handleSaveSettings} className="bg-green-600 text-white font-bold px-8 py-3 rounded-lg hover:bg-green-700 transition-transform transform hover:scale-105">שמור שינויים</button>
            </div>
        </div>
    );
};

// --- רכיב עליון שעוטף הכל ב-Providers ---
const SettingsPage = () => {
    const [user, setUser] = useState(undefined);

    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
        });
        return () => unsubscribe();
    }, []);

    if (user === undefined) {
        return <div>טוען...</div>;
    }
    if (!user) {
        return <LoginPage />;
    }

    return (
        <CacheProvider value={cacheRtl}>
            <ThemeProvider theme={theme}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div className="bg-gray-900 min-h-screen">
                        <SettingsContent userId={user.uid} />
                    </div>
                </LocalizationProvider>
            </ThemeProvider>
        </CacheProvider>
    );
};

export default SettingsPage;